import tw from 'twin.macro'
import styled from 'styled-components'
import { Nav, NavContainer } from '@/components/navbar'
import Paths from '@/config/paths.js'
import { Hero } from '@/components/headers'
import Features from '@/components/features/ThreeColSimple.js'
import { PrimaryButtonWhite } from '@/styles/Buttons.js'
import { BlogDetails } from '@/components/blog/BlogDetails'
import ContactMe from '@/components/cta/DownloadApp.js'
import Footer from '@/components/footers/Footer.js'
import emailIcon from '@/images/icons/email-icon.svg'
import phoneIcon from '@/images/icons/phone-icon.svg'
import formalPhoto from '@/images/formal-head-shot.jpg'
import tampaHouse from '@/images/tampa-house-water-cr.jpg'
import mapLocation from '@/images/map-location.svg'
import houseSun from '@/images/icons/house-sun-icon.svg'
import houseWater from '@/images/icons/house-water-icon.svg'
import { AboutDetails } from '@/pages/AboutPage'
import { BlogPropertyTaxes2024Details } from '@/pages/blog'
import { GetStartedCenteredBtn } from '@/components/buttons'

const HomePageStyled = styled.div`
  p,
  ul,
  li {
    ${tw`
      md:text-xl
  `}
  }
`

const MobilePadding = tw.div`px-5 lg:px-0`

const featureCards = [
  {
    imageSrc: houseWater,
    title: 'Find a home',
    description: 'Connect with me to find the home of your dreams',
    url: Paths.BuyHome.loc
  },
  {
    imageSrc: houseSun,
    title: 'Sell your home',
    description: 'Find the right buyer for your home',
    url: Paths.SellHome.loc
  },
  {
    imageSrc: mapLocation,
    title: 'Relocate',
    description: 'Let me help you relocate to Tampa Bay',
    url: Paths.Relocate.loc
  },
]

const AboutDetailsHome = {
  title: 'About Me: Get to Know Edith Austin',
  description:
    'Discover my journey from relocating to Tampa Bay to becoming a trusted expert in the area. Learn about my dedication to helping clients find homes that fit their lifestyles, my knowledge of the area’s unique statistics, and how my personalized approach has made a difference for buyers and sellers alike.',
  path: AboutDetails.path,
  thumbnailImgStr: AboutDetails.thumbnailImgStr,
}

const BlogPropertyTaxes2024DetailsHome = {
  title: 'How to Save Money on Property Taxes in Tampa Bay, Florida',
  description:
    'Uncover valuable tips and strategies to reduce your property taxes and maximize your savings.',
  path: BlogPropertyTaxes2024Details.path,
  thumbnailImgStr: BlogPropertyTaxes2024Details.thumbnailImgStr,
}

export default () => {
  return (
    <HomePageStyled>
      <NavContainer>
        <Nav />
      </NavContainer>

      <Hero title="Your Data-Driven Guide to Tampa Bay Real Estate" />

      <section>
        <div tw="max-w-screen-xl m-auto py-16">
          <MobilePadding>
            <h2 tw="text-center mt-8 mb-10 md:mb-20">
              Connecting You to the Home You Deserve Across Tampa Bay
            </h2>
          </MobilePadding>
          <div tw="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <img
                src={formalPhoto}
                alt="Edith Austin, Broker Associate in Tampa Bay"
                tw="rounded-lg m-auto max-w-[20rem] lg:max-w-[26rem] mt-4 mb-4 lg:mb-0"
              />
            </div>
            <div>
              <MobilePadding>
                <p tw="leading-loose">
                  Hi, I’m <strong>Edith Austin</strong>, a dedicated <strong>Florida Real Estate Broker Associate (C2EX)</strong> bringing a data-driven, analytical approach to buying and selling homes across the greater Tampa Bay region. Whether you are searching for a <strong>waterfront retreat, downtown luxury condo, suburban family home, or investment property</strong>, I guide you with precise market insights and personalized service.
                  <br />
                  <br />
                  Having navigated my own cross-country relocation to Florida, I specialize in <strong>residential resales, relocation services, and new home sales</strong> across <strong>Hillsborough, Pinellas, Pasco, Manatee, and Sarasota counties</strong>. Services are available in both <strong>English and Spanish (Se habla español)</strong> to ensure seamless communication through every step of your transaction.
                </p>
              </MobilePadding>
            </div>
          </div>
        </div>
      </section>

      {/* Structured Service Areas & Specialty Breakdown for SEO */}
      <section tw="py-20 border-t border-b border-gray-200">
        <div tw="max-w-screen-xl m-auto px-4">
          <h3 tw="text-center text-2xl font-bold mb-6">Serving 5 Counties Across Greater Tampa Bay</h3>
          <div tw="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
            <div>
              <strong>Hillsborough & Pasco</strong>
              <p tw="mt-1 text-gray-600">Tampa, South Tampa, Ybor City, Brandon, Riverview, Valrico, Seffner, Mango, Plant City, Lutz, Odessa, Westchase, Wesley Chapel, Land O' Lakes, New Port Richey</p>
            </div>
            <div>
              <strong>Pinellas County</strong>
              <p tw="mt-1 text-gray-600">St. Petersburg, St. Pete Beach, Clearwater, Clearwater Beach, Dunedin, Palm Harbor, Tarpon Springs, Largo, Seminole, Safety Harbor, Treasure Island, Madeira Beach</p>
            </div>
            <div>
              <strong>Manatee & Sarasota</strong>
              <p tw="mt-1 text-gray-600">Sarasota, Siesta Key, Bradenton, Bradenton Beach, Lakewood Ranch, Parrish, Ellenton, Palmetto, Longboat Key, Anna Maria, North Port</p>
            </div>
          </div>
        </div>
      </section>

      <section tw="bg-[#c4e1f2] py-20">
        <MobilePadding>
          <h2 tw="text-center my-6">
            Buying, Selling, or Relocating? Let Me Guide You Home in Tampa Bay
          </h2>
        </MobilePadding>
        <Features cards={featureCards} />
        <GetStartedCenteredBtn tw="lg:mt-0" variant='white'/>
      </section>

      <section>
        <MobilePadding>
          <div tw="max-w-screen-xl m-auto pt-8 pb-4 lg:py-16">
            <h2 tw="text-center mt-8 mb-4">
              Explore Market Insights & Guides
            </h2>
            <p tw="max-w-screen-lg m-auto text-center mb-0">
              Check out these articles to gain quantitative insights, local property tax updates, and neighborhood details before making your next move.
            </p>
            <BlogDetails details={AboutDetailsHome} imgMaxHeight="11rem" />
            <BlogDetails
              details={BlogPropertyTaxes2024DetailsHome}
              imgMaxWidth="10rem"
            />
            <br /> <br />
          </div>
        </MobilePadding>
      </section>

      <section id="contact-me" tw="overflow-hidden">
        <ContactMe
          text={
            <>
              Let's connect to discuss your real estate goals—whether you're moving across town or relocating across the country. 
              <br />
              <br />
              Contact me today for personalized market analysis and expert representation.
            </>
          }
          link1Text="(805) 765-1625"
          link1Url="tel:805-765-1625"
          link1IconSrc={phoneIcon}
          link2Text="edith@edithaustin.com"
          link2Url="mailto:edith@edithaustin.com"
          link2IconSrc={emailIcon}
          imageSrc={tampaHouse}
        />
      </section>

      <Footer />
    </HomePageStyled>
  )
}